import type { GetListResponse } from '@gokrew/models/generic';

/** The backend's `PaginationDto` refuses a `limit` above 100. */
export const MAX_PAGE_LIMIT = 100;

/**
 * Every row of a paginated list, page after page until `hasNextPage` is
 * false, merged into one response of the same shape (meta of the last page,
 * `hasNextPage` false). For bounded lists a screen must see whole: locations,
 * departments, job titles, admins, leave types. Never for employees: a
 * company can have thousands, use an infinite query or fetch by ids instead.
 */
export async function fetchAllPages<T, R extends GetListResponse<T>>(
  fetchPage: (page: number, limit: number) => Promise<R>
): Promise<R> {
  const first = await fetchPage(1, MAX_PAGE_LIMIT);
  const rows = [...(first.data?.data ?? [])];
  let last = first;
  while (last.data?.meta?.hasNextPage && last.data.data.length > 0) {
    last = await fetchPage(last.data.meta.page + 1, MAX_PAGE_LIMIT);
    rows.push(...(last.data?.data ?? []));
  }
  return {
    ...last,
    data: {
      ...last.data,
      data: rows,
      meta: { ...last.data?.meta, hasNextPage: false },
    },
  } as R;
}

/** Ids per lookup request: keeps the query string well under 8 KB. */
const ID_CHUNK_SIZE = 50;

/**
 * `ids` in chunks the API accepts, each chunk one request, rows concatenated.
 * For display-only lookups of people by id (names on a summary).
 */
export async function fetchByIdChunks<T>(
  ids: string[],
  fetchChunk: (chunk: string[], limit: number) => Promise<T[]>
): Promise<T[]> {
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += ID_CHUNK_SIZE) {
    chunks.push(unique.slice(i, i + ID_CHUNK_SIZE));
  }
  const results = await Promise.all(
    chunks.map((chunk) => fetchChunk(chunk, ID_CHUNK_SIZE))
  );
  return results.flat();
}
